import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import heroMethod from "@/assets/hero-method.jpg";
import { formatSiteDateLong } from "@/lib/datetime.schedule";
import { ArrowLeft } from "lucide-react";
import { getBlogPostBySlug } from "@/lib/blog.functions";
import { resolveBlogShareImageUrl } from "@/lib/blog-helpers";
import { blogPostJsonLd, pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";

const MarkdownContent = lazy(() =>
  import("@/components/blog/MarkdownContent").then((m) => ({ default: m.MarkdownContent })),
);

type BlogPost = Awaited<ReturnType<typeof getBlogPostBySlug>>;

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    try {
      return await getBlogPostBySlug({ data: { slug: params.slug } });
    } catch {
      return null;
    }
  },
  head: ({ loaderData }) => {
    const post = loaderData as unknown as BlogPost | null;
    const ogImage = post
      ? resolveBlogShareImageUrl({
          shareImageUrl: post.share_image_url,
          bodyMarkdown: post.body_markdown,
          defaultImageUrl: heroMethod,
        })
      : heroMethod;
    const title = `${post?.title ?? "Blog"} | Mark Egly Golf`;
    const description = post?.excerpt ?? "Blog posts from Mark Egly Golf in Des Moines, Iowa.";
    const path = `/blog/${post?.slug ?? ""}`;

    return pageHead({
      title,
      description,
      path,
      image: ogImage,
      type: "article",
      // Post body is DB English; do not claim translated alternates or locale-prefixed canonicals.
      hreflang: false,
      locale: "en",
      jsonLd: post
        ? blogPostJsonLd({
            title: post.title,
            description,
            path,
            image: ogImage,
            publishedAt: post.published_at,
            updatedAt: post.updated_at,
          })
        : undefined,
    });
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { slug } = Route.useParams();
  const loaderPost = Route.useLoaderData();
  const postQ = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: () => getBlogPostBySlug({ data: { slug } }),
    initialData: loaderPost ?? undefined,
    enabled: !!slug,
  });

  const post = postQ.data;

  if (postQ.isLoading) {
    return (
      <section className="container-page page-band-pad">
        <p className="text-foreground/60">{m.common_loading()}</p>
      </section>
    );
  }

  if (!post) {
    return (
      <section className="container-page page-band-pad">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-ember hover:text-ember/80"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {m.blog_back()}
        </Link>
        <h1 className="mt-8 font-display text-4xl">{m.blog_post_not_found()}</h1>
        <p className="mt-3 text-foreground/60">{m.blog_post_missing()}</p>
      </section>
    );
  }

  return (
    <>
      <section className="bg-ink">
        <div className="container-page page-hero-pad">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-ember"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {m.blog_back()}
          </Link>
          <div className="mt-6 font-display text-xs tracking-[0.18em] text-ember">
            {formatSiteDateLong(post.published_at)}
          </div>
          <h1 className="mt-3 max-w-3xl font-display text-5xl leading-[0.95] md:text-6xl">
            {post.title}
          </h1>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <div className="mx-auto max-w-3xl">
          <Suspense fallback={null}>
            <MarkdownContent content={post.body_markdown} />
          </Suspense>
        </div>
      </section>
    </>
  );
}
